"use client";

import { useEffect, useState } from "react";
import { sanitizeSurfaceTitle } from "@/lib/savedResourceTitles";
import type { HomeInterpretation, HomeRelatedGoal, GoalDraft, TaskBreakdown, ThoughtCompression, CaptureRouteProposal } from "@/lib/api";
import { LIFE_AREA_KEYS, LIFE_AREA_META as CANONICAL_LIFE_AREA_META } from "@/lib/lifeAreas";

const LIFE_AREA_META: Record<string, { emoji: string; label: string; color: string }> = Object.fromEntries(
  LIFE_AREA_KEYS.map((k) => [
    k,
    {
      emoji: CANONICAL_LIFE_AREA_META[k].emoji,
      label: CANONICAL_LIFE_AREA_META[k].label,
      color: CANONICAL_LIFE_AREA_META[k].rail,
    },
  ]),
);

export type CaptureType =
  | "task"
  | "decision"
  | "reference"
  | "goal"
  | "schedule_block"
  | "capture_only";

const KNOWN_ROUTE_KINDS: ReadonlyArray<CaptureType> = [
  "schedule_block",
  "decision",
  "goal",
  "task",
  "reference",
  "capture_only",
];

function classifyCapture(
  _primaryType: string | undefined,
  recommendedRouteKind: string | null,
  _sourceText: string
): CaptureType {
  // U13 / Audit fix #1: render strictly from backend route truth.
  // No more parallel reinterpretation from primary_type or sourceText
  // heuristics, no more silent default to "task" when uncertain. The
  // backend has already classified — the popup must show that, not a
  // second opinion that pressures the user toward action.
  if (recommendedRouteKind && (KNOWN_ROUTE_KINDS as string[]).includes(recommendedRouteKind)) {
    return recommendedRouteKind as CaptureType;
  }
  // No route truth yet (race or pre-route render). Default to
  // capture_only — preserve-first, not commit-first. The user can
  // override via the "Wrong type?" pill row if the route truth
  // arrives wrong or never arrives.
  return "capture_only";
}

function isSchedulePlacementIntent(
  interpretation: HomeInterpretation | null,
  sourceText: string
): boolean {
  const normalized = sourceText.trim().toLowerCase();
  if (!normalized) return false;
  if (interpretation?.primary_type === "daily_plan") return true;
  return /\b(block|schedule|timebox)\b/.test(normalized)
    && /\b(today|tomorrow|tonight|morning|afternoon|evening)\b/.test(normalized);
}

function typeLabel(type: CaptureType): string {
  switch (type) {
    case "task": return "Task";
    case "decision": return "Decision";
    case "reference": return "Reference";
    case "goal": return "Goal";
    case "schedule_block": return "Scheduled block";
    // Popup now offers Save to Library + Create goal — frame it as a
    // destination question rather than a "Captured" terminal label.
    case "capture_only": return "Save";
  }
}

function typeColor(type: CaptureType): string {
  switch (type) {
    case "task": return "bg-forest";
    case "decision": return "bg-blue";
    case "reference": return "bg-amber";
    case "goal": return "bg-[#6366F1]";
    // Sky tone — distinct from decision blue but readable as time/calendar.
    case "schedule_block": return "bg-[#0EA5E9]";
    // Forest-med matches the Today brand and reads as actionable, since
    // this state now requires the user to pick Library or Goal.
    case "capture_only": return "bg-forest-med";
  }
}

function typeDescription(type: CaptureType): string {
  switch (type) {
    case "task": return "This looks like something you can act on.";
    case "decision": return "This is a question or choice to think through.";
    case "reference": return "This is something worth saving for later.";
    case "goal": return "This could become a goal to work toward.";
    case "schedule_block": return "This carries concrete timing — better on the Plan than buried in your todo list.";
    case "capture_only": return "Pick where this should land — save it to your Library or turn it into a goal.";
  }
}

function successMessage(type: CaptureType): string {
  switch (type) {
    case "task": return "Added to your tasks.";
    case "decision": return "Saved to Decisions.";
    case "reference": return "Saved to Library.";
    case "goal": return "Goal created.";
    case "schedule_block": return "Placed on your Plan.";
    // Unreachable now (capture_only branch commits as reference or goal),
    // but kept for the exhaustive switch on CaptureType.
    case "capture_only": return "Saved.";
  }
}

export interface CapturePopupData {
  sourceText: string;
  interpretation: HomeInterpretation | null;
  goalDraft: GoalDraft | null;
  taskBreakdown: TaskBreakdown | null;
  relatedGoal: HomeRelatedGoal | null;
  compression: ThoughtCompression | null;
  action: string | null;
  recommendedRouteKind: string | null;
  routeProposals: CaptureRouteProposal[];
  captureId: string | null;
  isError: boolean;
  agentMessage: string;
  previewTitle?: string | null;
}

interface Props {
  data: CapturePopupData;
  actionLoading: string | null;
  onCorrectType?: (type: CaptureType) => void | Promise<void>;
  onDoToday: (lifeArea: string | null) => void;
  onPlaceInPlan: (lifeArea: string | null) => void;
  onSaveForLater: () => void;
  onOpenDecision: () => void;
  onSaveToLibrary: (lifeArea: string | null) => void;
  onCreateGoal: (lifeArea: string | null) => void;
  onTalkItThrough: () => void;
  /** U13: capture_only commit — preserve the thought without promoting
   * it to task/goal/decision/reference. Marks capture as `retained`
   * via the backend's commit_capture_item capture_only branch. */
  onSaveAsCapture: () => void;
  onDismiss: () => void;
  /**
   * Intent Preview state for the related-goal suggestion (U3.3).
   * - "accepted": user has opted in (auto-attach mode starts here;
   *   suggested mode gets here after tapping ✓)
   * - "dismissed": user tapped "Not this one" / ✗
   * - "pending": suggested mode, awaiting user decision
   */
  goalDecision: "accepted" | "dismissed" | "pending";
  onGoalAccept: () => void;
  onGoalDismiss: () => void;
}

export default function CaptureResultPopup({
  data,
  actionLoading,
  onCorrectType,
  onDoToday,
  onPlaceInPlan,
  onSaveForLater,
  onOpenDecision,
  onSaveToLibrary,
  onCreateGoal,
  onTalkItThrough,
  onSaveAsCapture,
  onDismiss,
  goalDecision,
  onGoalAccept,
  onGoalDismiss,
}: Props) {
  const captureType = classifyCapture(
    data.interpretation?.primary_type,
    data.recommendedRouteKind,
    data.sourceText
  );

  // Detect life area from AI-matched goal or goal draft
  const detectedLifeArea = data.relatedGoal?.category
    || data.goalDraft?.category
    || null;

  const [overrideType, setOverrideType] = useState<CaptureType | null>(null);
  const [showOverride, setShowOverride] = useState(false);
  const [correctionLoadingType, setCorrectionLoadingType] = useState<CaptureType | null>(null);
  const [done, setDone] = useState<CaptureType | null>(null);
  const [selectedLifeArea, setSelectedLifeArea] = useState<string | null>(detectedLifeArea);
  const activeType = overrideType ?? captureType;
  const schedulePlacementIntent =
    activeType === "task" && isSchedulePlacementIntent(data.interpretation, data.sourceText);
  const areaMeta = selectedLifeArea ? LIFE_AREA_META[selectedLifeArea] : null;
  const confidence = data.interpretation?.confidence ?? 0;
  const interpretation = data.interpretation?.interpretation || data.compression?.summary || null;
  const relatedGoal = data.relatedGoal;
  const isUrl = /^https?:\/\//.test(data.sourceText.trim());
  const normalizedSourceText = isUrl ? data.sourceText.trim() : data.sourceText.slice(0, 120);
  const titleByType: Record<CaptureType, string> = {
    task:
      data.previewTitle
      || data.taskBreakdown?.title
      || data.action
      || normalizedSourceText
      || "Captured item",
    goal:
      data.goalDraft?.title
      || data.previewTitle
      || data.action
      || normalizedSourceText
      || "Captured item",
    decision: normalizedSourceText || data.action || "Decision to explore",
    reference:
      data.previewTitle
      || data.compression?.top_themes?.[0]
      || normalizedSourceText
      || "Saved reference",
    // schedule_block reuses the task title — same underlying object.
    schedule_block:
      data.previewTitle
      || data.taskBreakdown?.title
      || data.action
      || normalizedSourceText
      || "Captured item",
    // Preserve the raw source — capture_only's job is fidelity, not
    // promotion to a stronger title.
    capture_only: normalizedSourceText || data.action || "Captured thought",
  };
  const taskTitle = titleByType[activeType];
  const firstStep =
    activeType === "task" || activeType === "schedule_block"
      ? data.taskBreakdown?.first_action
      : activeType === "goal"
        ? data.goalDraft?.first_action
        : null;
  const isBusy = actionLoading !== null || correctionLoadingType !== null;

  useEffect(() => {
    setOverrideType(null);
    setCorrectionLoadingType(null);
  }, [data.captureId, data.recommendedRouteKind, data.goalDraft?.title, data.taskBreakdown?.title]);

  // Escape key closes the modal (WCAG 2.1 dialog a11y).
  // Matches the pattern in RoadmapModal.tsx. Dogfood R3 bug #8.
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.key === "Escape") onDismiss();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onDismiss]);

  const handleAction = async (type: CaptureType, fn: () => void | Promise<void>) => {
    try {
      await fn();
      setDone(type);
      setTimeout(onDismiss, 1500);
    } catch {
      // Action failed — keep popup open, don't show success
    }
  };

  const handleTypeCorrection = async (type: CaptureType) => {
    if (!data.captureId || !onCorrectType) {
      setOverrideType(type);
      setShowOverride(false);
      return;
    }

    if (type === captureType && overrideType == null) {
      setShowOverride(false);
      return;
    }

    setOverrideType(type);
    setCorrectionLoadingType(type);
    try {
      await onCorrectType(type);
      setShowOverride(false);
      setOverrideType(null);
    } catch {
      setOverrideType(null);
    } finally {
      setCorrectionLoadingType(null);
    }
  };

  // Error state
  if (data.isError) {
    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/25 backdrop-blur-[2px]" onClick={onDismiss}>
        <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
          <p className="text-sm text-amber">{data.agentMessage}</p>
          <button onClick={onDismiss} className="mt-4 w-full rounded-full bg-forest-med py-2.5 text-sm font-medium text-white">OK</button>
        </div>
      </div>
    );
  }

  // Success state — brief confirmation then auto-close
  if (done) {
    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/25 backdrop-blur-[2px]" onClick={onDismiss}>
        <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-2xl text-center" onClick={(e) => e.stopPropagation()}>
          <div className={`mx-auto flex h-12 w-12 items-center justify-center rounded-full ${typeColor(done)}`}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
              <path d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <p className="mt-3 text-sm font-medium text-text">{successMessage(done)}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/25 backdrop-blur-[2px]" onClick={onDismiss}>
      <div
        className="w-full max-w-md rounded-2xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="capture-result"
      >
        {/* Color header */}
        <div className={`h-2 rounded-t-2xl ${typeColor(activeType)}`} />

        <div className="p-5">
          {/* Type + confidence */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold text-white ${typeColor(activeType)}`}>
                {typeLabel(activeType)}
              </span>
              {confidence > 0 && <span className="text-[10px] text-muted">{Math.round(confidence * 100)}%</span>}
            </div>
            <button
              onClick={onDismiss}
              aria-label="Close capture proposal"
              className="flex h-7 w-7 items-center justify-center rounded-full text-muted hover:bg-secondary hover:text-text"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M18 6L6 18M6 6l12 12" /></svg>
            </button>
          </div>

          {/* Title */}
          <p className="mt-3 text-base font-semibold text-text leading-snug break-all">
            {isUrl ? taskTitle : sanitizeSurfaceTitle(taskTitle)}
          </p>

          {/* AI interpretation */}
          {interpretation ? (
            <p className="mt-2 text-sm text-text-light leading-relaxed">{interpretation}</p>
          ) : (
            <p className="mt-2 text-sm text-muted leading-relaxed">{typeDescription(activeType)}</p>
          )}

          {/* Related goal — three-band Intent Preview (U3.3).
              - auto + accepted → "Goal: X" with "Not this one" dismiss
              - auto + dismissed → hidden (user opted out)
              - suggested + pending → accept/dismiss chip
              - suggested + accepted → same as auto + accepted
              - suggested + dismissed → hidden */}
          {relatedGoal && goalDecision !== "dismissed" && (
            <div
              className="mt-2 inline-flex items-center gap-2"
              data-testid="capture-related-goal"
              data-link-mode={relatedGoal.linkMode}
              data-decision={goalDecision}
            >
              {goalDecision === "accepted" ? (
                <>
                  <span className="text-[11px] text-muted">Goal:</span>
                  <span className="text-[11px] font-medium text-text">
                    {sanitizeSurfaceTitle(relatedGoal.title)}
                  </span>
                  <button
                    type="button"
                    onClick={onGoalDismiss}
                    className="text-[11px] text-muted hover:text-text underline underline-offset-2"
                    data-testid="capture-goal-dismiss"
                  >
                    Not this one
                  </button>
                </>
              ) : (
                /* suggested + pending */
                <>
                  <span className="text-[11px] text-muted">
                    Link to {sanitizeSurfaceTitle(relatedGoal.title)}?
                  </span>
                  <button
                    type="button"
                    onClick={onGoalAccept}
                    className="rounded-full border border-forest-med bg-forest-med/5 px-2 py-0.5 text-[11px] font-medium text-forest-med hover:bg-forest-med/10"
                    data-testid="capture-goal-accept"
                  >
                    Yes
                  </button>
                  <button
                    type="button"
                    onClick={onGoalDismiss}
                    className="rounded-full border border-border px-2 py-0.5 text-[11px] font-medium text-text-light hover:bg-bg"
                    data-testid="capture-goal-reject"
                  >
                    No
                  </button>
                </>
              )}
            </div>
          )}

          {/* Life area picker — always visible */}
          <div className="mt-3">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-muted mb-1.5">Life area</p>
              <div className="flex flex-wrap gap-1.5">
                {Object.entries(LIFE_AREA_META).map(([key, meta]) => {
                  const active = selectedLifeArea === key;
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setSelectedLifeArea(active ? null : key)}
                      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-medium transition-colors ${
                        active ? `${meta.color} text-white` : "border border-border text-text-light hover:bg-bg"
                      }`}
                    >
                      {meta.emoji} {meta.label}
                    </button>
                  );
                })}
              </div>
            </div>

          {/* First step */}
          {firstStep && (activeType === "task" || activeType === "goal" || activeType === "schedule_block") && (
            <div className="mt-3 rounded-xl bg-bg px-3 py-2.5">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-muted">First step</p>
              <p className="mt-1 text-sm text-text">{sanitizeSurfaceTitle(firstStep)}</p>
            </div>
          )}

          {/* Override */}
          <div className="mt-4">
            {!showOverride ? (
              <button
                type="button"
                onClick={() => setShowOverride(true)}
                disabled={isBusy}
                className="text-[11px] font-medium text-muted hover:text-text disabled:opacity-50"
              >
                Or treat as:
              </button>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {(["schedule_block", "task", "decision", "reference", "goal"] as const).map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => void handleTypeCorrection(t)}
                    disabled={isBusy}
                    className={`rounded-full px-2.5 py-1 text-[11px] font-medium transition-colors ${
                      activeType === t ? `${typeColor(t)} text-white` : "border border-border text-text-light hover:bg-bg"
                    } disabled:opacity-50`}
                  >
                    {typeLabel(t)}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="mt-5 flex gap-2">
            {activeType === "capture_only" && (
              <>
                <button
                  onClick={() => handleAction("reference", () => onSaveToLibrary(selectedLifeArea))}
                  disabled={isBusy}
                  data-testid="capture-save-to-library"
                  className="flex-1 rounded-full bg-amber py-2.5 text-sm font-medium text-white hover:bg-amber/90 disabled:opacity-50"
                >
                  {actionLoading === "reference" ? "Saving..." : "Save to Library"}
                </button>
                <button
                  onClick={() => handleAction("goal", () => onCreateGoal(selectedLifeArea))}
                  disabled={isBusy}
                  data-testid="capture-create-goal"
                  className="rounded-full border border-border px-4 py-2.5 text-sm font-medium text-text-light hover:bg-bg disabled:opacity-50"
                >
                  {actionLoading === "goal" ? "Creating..." : "Create goal"}
                </button>
              </>
            )}
            {activeType === "schedule_block" && (
              <>
                {/* U11: route truth says this belongs on the Plan.
                    onPlaceInPlan is wired to the new schedule_block
                    atomic commit path in TodayScreen — one round-trip,
                    no secondary createScheduleBlock write. */}
                <button
                  onClick={() => handleAction("schedule_block", () => onPlaceInPlan(selectedLifeArea))}
                  disabled={isBusy}
                  data-testid="capture-place-on-plan"
                  className="flex-1 rounded-full bg-[#0EA5E9] py-2.5 text-sm font-medium text-white hover:bg-[#0284C7] disabled:opacity-50"
                >
                  {actionLoading === "timebox" || actionLoading === "schedule_block"
                    ? "Placing..."
                    : "Place in Plan"}
                </button>
                <button
                  onClick={async () => {
                    // Strict UAT 2026-05-24 (P0 P*-D3-013): the
                    // previous implementation only switched activeType
                    // (a UI mode flip) and did NOT commit the task.
                    // The user landed in a half-state where the popup
                    // re-rendered with "Do today"/"Later" but no row
                    // existed yet — and the strict UAT (which mirrors
                    // a real user clicking once) saw the popup stay
                    // open with the task never appearing on Today.
                    // Fix: correct the type AND commit as a task in
                    // one click. If the user wanted to demote without
                    // committing, the "🏃 Personal/etc" life-area
                    // pills above still re-route the proposal.
                    try {
                      await handleTypeCorrection("task");
                    } catch {
                      // type correction failed (offline?) — fall
                      // through so we still try to commit as task.
                    }
                    await handleAction("task", () =>
                      onDoToday(selectedLifeArea)
                    );
                  }}
                  disabled={isBusy}
                  data-testid="capture-demote-to-todo"
                  className="rounded-full border border-border px-4 py-2.5 text-sm font-medium text-text-light hover:bg-bg disabled:opacity-50"
                >
                  {actionLoading === "task" ? "Adding..." : "Keep as todo"}
                </button>
              </>
            )}
            {activeType === "task" && (
              <>
                <button
                  onClick={() =>
                    handleAction(
                      "task",
                      () =>
                        schedulePlacementIntent
                          ? onPlaceInPlan(selectedLifeArea)
                          : onDoToday(selectedLifeArea)
                    )
                  }
                  disabled={isBusy}
                  className="flex-1 rounded-full bg-forest-med py-2.5 text-sm font-medium text-white hover:bg-forest disabled:opacity-50">
                  {actionLoading === "timebox"
                    ? "Placing..."
                    : actionLoading === "task"
                      ? "Adding..."
                      : schedulePlacementIntent
                        ? "Place in Plan"
                        : "Do today"}
                </button>
                <button onClick={() => handleAction("reference", onSaveForLater)} disabled={isBusy}
                  className="rounded-full border border-border px-4 py-2.5 text-sm font-medium text-text-light hover:bg-bg disabled:opacity-50">
                  Later
                </button>
              </>
            )}
            {activeType === "decision" && (
              <>
                <button onClick={() => handleAction("decision", onOpenDecision)} disabled={isBusy}
                  className="flex-1 rounded-full bg-blue py-2.5 text-sm font-medium text-white hover:bg-blue/90 disabled:opacity-50">
                  {actionLoading === "decision" ? "Saving..." : "Save decision"}
                </button>
                <button onClick={() => handleAction("task", onTalkItThrough)}
                  className="rounded-full border border-border px-4 py-2.5 text-sm font-medium text-text-light hover:bg-bg">
                  Chat
                </button>
              </>
            )}
            {activeType === "reference" && (
              <button onClick={() => handleAction("reference", () => onSaveToLibrary(selectedLifeArea))} disabled={isBusy}
                className="flex-1 rounded-full bg-amber py-2.5 text-sm font-medium text-white hover:bg-amber/90 disabled:opacity-50">
                {actionLoading === "reference" ? "Saving..." : "Save to Library"}
              </button>
            )}
            {activeType === "goal" && (
              <>
                <button onClick={() => handleAction("goal", () => onCreateGoal(selectedLifeArea))} disabled={isBusy}
                  className="flex-1 rounded-full bg-[#6366F1] py-2.5 text-sm font-medium text-white hover:bg-[#6366F1]/90 disabled:opacity-50">
                  {actionLoading === "goal" ? "Creating..." : "Create goal"}
                </button>
                <button onClick={() => handleAction("task", () => onDoToday(selectedLifeArea))} disabled={isBusy}
                  className="rounded-full border border-border px-4 py-2.5 text-sm font-medium text-text-light hover:bg-bg disabled:opacity-50">
                  As task
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
